// PageDescription: 실시간 의료 상담 상세 페이지
import { dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import React from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import NotFoundTemplate from '@/components/templates/error/notFound/NotFoundTemplate';
import QnaDetailMobileTemplate from '@/components/templates/mobile/communication/Qna/QnaDetailMobileTemplate';
import QnaDetailSkeleton from '@/components/templates/mobile/communication/Qna/QnaDetailMobileTemplate/QnaDetailSkeleton';
import { SIX_HOURS_IN_MILLISECONDS } from '@/constants/common';
import useAppInstallTopBanner from '@/hooks/Cuj/useAppInstallTopBanner';
import useGetBannerAdExchange from '@/hooks/ad/useGetBannerAdExchange';
import useGetQnaDetailWithNearSimplyQuestions from '@/hooks/qna/useGetQnaDetailWithNearSimplyQuestions';
import useQuestionDetailViewModel from '@/hooks/qna/useQuestionDetailViewModel';
import useGetRelatedQna from '@/hooks/qna/useRelatedQna';
import useScreenFocus from '@/hooks/useScreenFocus';
import { AdPageType } from '@/models/ad/list';
import { ExtendedQueryClient } from '@/models/common/query-client';
import { ssrQueryCache } from '@/models/common/ssr-cache';
import { CustomPageProps } from '@/types/app';
import { AnswererType, QuestionAccessibleType } from '@/types/bff/gql-schema';
import { ROUTES } from '@/types/common';
import { GraphqlError, ServerError } from '@/types/error';
import { QnaDetailVo } from '@/types/qna';
import { formatShareUrl } from '@/utils/format';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import dayjs from 'dayjs';
import { NextSeo } from 'next-seo';
import Head from 'next/head';

const createQnaDetailLdJson = (qnaDetail: QnaDetailVo) => {
  const convertAnswererTypeToAnswererName = (answererType: AnswererType) => {
    switch (answererType) {
      case AnswererType.Nurse:
        return 'Nurse';
      case AnswererType.Ai:
        return 'AI 상담 도우미';
      case AnswererType.Doctor:
        return 'MedicalDoctor';
    }
  };

  if (!qnaDetail.answers || qnaDetail.answers.length === 0) {
    return {};
  }

  const foundedNurseAnswer = qnaDetail.answers.find((answer) => answer.answerer.answererType === AnswererType.Nurse);

  const foundedAiAnswer = qnaDetail.answers.find((answer) => answer.answerer.answererType === AnswererType.Ai);
  const foundedDoctorAnswer = qnaDetail.answers.find((answer) => answer.answerer.answererType === AnswererType.Doctor);

  const foundedAcceptedAnswer = foundedNurseAnswer || foundedAiAnswer || foundedDoctorAnswer;
  const suggestedAnswers = qnaDetail.answers.filter((answer) => answer.answerPid !== foundedAcceptedAnswer?.answerPid);

  const questionSchema = {
    '@type': 'Question',
    '@id': `${process.env.NEXT_PUBLIC_DOMAIN}/content/qna/${qnaDetail.questionPid}#question`,
    name: qnaDetail.title,
    text: qnaDetail.content,
    dateCreated: dayjs(qnaDetail.createdAt).format('YYYY-MM-DDTHH:mm:ss[Z]'),
    author: {
      '@type': 'Person',
      name: qnaDetail.user.name,
    },
    answerCount: qnaDetail.answers.length,
    ...(suggestedAnswers.length && {
      suggestedAnswer: suggestedAnswers.map((suggestedAnswer) => ({
        '@type': 'Answer',
        text: suggestedAnswer.content,
        upvoteCount: suggestedAnswer.likeCount,
        dateCreated: dayjs(suggestedAnswer.createdAt).format('YYYY-MM-DDTHH:mm:ss[Z]'),
        author: {
          '@type': 'Person',
          name: suggestedAnswer.answerer.name,
          jobTitle: convertAnswererTypeToAnswererName(suggestedAnswer.answerer.answererType),
          ...(suggestedAnswer.answerer.answererType !== AnswererType.Doctor && {
            affiliation: {
              '@type': 'Organization',
              name: '닥터나우',
              url: `${process.env.NEXT_PUBLIC_DOMAIN}`,
            },
          }),
        },
      })),
    }),
    ...(foundedAcceptedAnswer && {
      acceptedAnswer: {
        '@type': 'Answer',
        text: foundedAcceptedAnswer.content,
        upvoteCount: foundedAcceptedAnswer.likeCount,
        url: `${process.env.NEXT_PUBLIC_DOMAIN}/content/qna/${qnaDetail.questionPid}`,
        dateCreated: dayjs(foundedAcceptedAnswer.createdAt).format('YYYY-MM-DDTHH:mm:ss[Z]'),
        author:
          foundedAcceptedAnswer.answerer.answererType == AnswererType.Nurse
            ? {
                '@type': 'Person',
                name: '닥터나우 의료진',
              }
            : {
                '@type': 'Person',
                name: foundedAcceptedAnswer.answerer.affiliatedInstitutionName,
              },
      },
    }),
  };

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'QAPage',
      '@id': `${process.env.NEXT_PUBLIC_DOMAIN}/content/qna/${qnaDetail.questionPid}`,
      url: `${process.env.NEXT_PUBLIC_DOMAIN}/content/qna/${qnaDetail.questionPid}`,
      mainEntity: questionSchema,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      url: `${process.env.NEXT_PUBLIC_DOMAIN}/content/qna/${qnaDetail.questionPid}`,
      name: qnaDetail.title,
      description: qnaDetail.content,
      author: {
        '@type': 'Person',
        name: qnaDetail.user.name,
      },
      datePublished: dayjs(qnaDetail.createdAt).format('YYYY-MM-DDTHH:mm:ss[Z]'),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '닥터나우',
      url: `${process.env.NEXT_PUBLIC_DOMAIN}`,
      logo: 'https://doctornow-front-images.s3.ap-northeast-2.amazonaws.com/og-main.png',
      contactPoint: [
        {
          '@type': 'ContactPoint',
          email: 'contact@doctornow.co.kr',
          contactType: 'customer service',
        },
      ],
    },
  ];
};

interface Props extends CustomPageProps {
  id: string;
}

const QnaDetail = ({ id, client }: Props) => {
  useScreenFocus();
  const viewModel = useQuestionDetailViewModel(client);
  const topBannerViewModel = useAppInstallTopBanner(client);

  if (viewModel.isQnaDetailLoading) {
    return <QnaDetailSkeleton />;
  }

  if (!viewModel.qnaDetail) return <NotFoundTemplate />;

  if (viewModel.isNotMine && viewModel.isPrivateQuestion) {
    return <NotFoundTemplate />;
  }

  const ldJson = createQnaDetailLdJson(viewModel.qnaDetail);

  return (
    <>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson) }} />
      </Head>
      <NextSeo
        noindex={viewModel.isPrivateQuestion}
        nofollow={viewModel.isPrivateQuestion}
        title={`${viewModel.qnaDetail.title} - 닥터나우`}
        description={`${viewModel.qnaDetail.content}`}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.QNA.DETAIL.path}/${id}`}
        openGraph={{
          title: `${viewModel.qnaDetail.title} - 닥터나우`,
          description: `${viewModel.qnaDetail.content}`,
          url: `${formatShareUrl(ROUTES.QNA.HOME.path)}/${id}`,
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_QNA_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
      <QnaDetailMobileTemplate
        viewModel={{ ...viewModel, qnaDetail: viewModel.qnaDetail }}
        client={client}
        topBannerViewModel={topBannerViewModel}
      />
    </>
  );
};

QnaDetail.Layout = DefaultLayout;

export default QnaDetail;

export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (ctx) => {
  const id = ctx.params?.id as string;

  if (!id) {
    return {
      redirect: {
        destination: ROUTES.QNA.REALTIME.path,
        permanent: true,
      },
    };
  }

  const queryClient = new ExtendedQueryClient();

  const cacheKey = ctx.req.url as string;

  try {
    const questionDetail = await useGetQnaDetailWithNearSimplyQuestions.fetchQuery(queryClient, id);

    if (questionDetail.questionV2?.accessibleType !== QuestionAccessibleType.All) {
      return {
        props: {
          id,
        },
      };
    }

    if (ssrQueryCache.has(cacheKey)) {
      queryClient.setQueryDataFromCache(ssrQueryCache.get(cacheKey) ?? []);
      await Promise.allSettled([
        useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.QnaDetail, ctx.client),
        useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.TopCtaBand, ctx.client),
      ]);

      return {
        props: {
          id,
          dehydratedState: dehydrate(queryClient),
        },
      };
    }

    await Promise.allSettled([
      useGetRelatedQna.prefetchQuery(queryClient, id),
      useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.QnaDetail, ctx.client),
      useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.TopCtaBand, ctx.client),
    ]);
  } catch (e: unknown) {
    const { code } = e as unknown as GraphqlError;
    if (code === ServerError.NotFound) {
      return {
        notFound: true,
      };
    }
    if (ctx.res && code === ServerError.BadRequest) {
      ctx.res.statusCode = 400;
    }

    return {
      notFound: true,
    };
  }

  ssrQueryCache.set(cacheKey, queryClient.getQueryClientData([useGetRelatedQna.getKey(id)]), SIX_HOURS_IN_MILLISECONDS);

  return {
    props: {
      id,
      dehydratedState: dehydrate(queryClient),
    },
  };
});
